import { ReactElement, useState } from 'react';
import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { toast } from 'react-toastify';
import { RequestStatus } from 'types/RequestStatus';
import ApiErrorDescription from 'components/common/ApiErrorDescription/ApiErrorDescription';

type usePostReturnType = [
	postData: (
		payload: any,
		requestOptions?: AxiosRequestConfig,
	) => Promise<{ success: boolean; data: any }>,
	status: RequestStatus,
	error?: AxiosError<any>,
];
export const POST = 'post';
export const PUT = 'put';
export const DELETE = 'delete';
type Method = 'post' | 'put' | 'delete';

const useSubmit = (
	apiRoute: string,
	method: Method = POST,
	toastOptions?: {
		messageOnSuccess: React.ReactElement | string | undefined;
		messageOnError: React.ReactElement | string | undefined;
		showApiError: boolean | undefined;
	},
): usePostReturnType => {
	const [status, setStatus] = useState(RequestStatus.UNLOADED);
	const [error, setError] = useState<AxiosError<any> | undefined>(undefined);
	const { messageOnSuccess, messageOnError, showApiError } =
		toastOptions || {};
	const postData = async (
		payload: any,
		requestOptions?: AxiosRequestConfig,
	) => {
		setError(undefined);
		setStatus(RequestStatus.LOADING);
		let success = false;
		let result;
		try {
			const axiosMethod = axios[method];
			result =
				method === DELETE
					? await axiosMethod(apiRoute, requestOptions)
					: await axiosMethod(apiRoute, payload);
			setStatus(RequestStatus.SUCCESS);
			success = true;
			messageOnSuccess &&
				toast.success(messageOnSuccess, { autoClose: 2000 });
		} catch (error) {
			setStatus(RequestStatus.ERROR);
			const axiosError = error as AxiosError<any>;
			setError(axiosError);
			messageOnError &&
				toast.error(
					getErrorContent(messageOnError, showApiError, axiosError),
				);
		}
		return {
			success,
			data: result && result.data,
		};
	};
	return [postData, status, error];
};

const getErrorContent = (
	messageOnError: React.ReactElement | string | undefined,
	showApiError: boolean | undefined,
	axiosError: AxiosError<any>,
): ReactElement => (
	<div>
		{messageOnError}
		{showApiError && (
			<div>
				<ApiErrorDescription error={axiosError} />
			</div>
		)}
	</div>
);
export default useSubmit;
